/**
 * @requires ./slick.js
 */

;( () => {

	document.addEventListener( 'alpine:init', () => {

		/**
		 * Slick slider.
		 */
		Alpine.data( 'slick', () => ({

			get sliderEl() {
				return this.$el;
			},

			get slidesEl() {
				return this.$el.querySelector( '.slides' );
			},

			get sliderName() {
				return this.$el.dataset.slick;
			},

			get slideCount() {
				return this.slidesEl.querySelectorAll( '.slide' ).length;
			},

			init() {

				const options = {
					appendArrows: this.sliderEl.querySelector( '.slick-arrows-container' ),
					infinite    : false,
					mobileFirst : true,
				};

				// Handle prefers-reduced-motion.
				if ( window.matchMedia( '( prefers-reduced-motion: reduce )' ).matches ) {
					options.fade = true;
				}

				// Custom options per slider.
				switch ( this.sliderName ) {

				case 'timeline':
					options.arrows       = true;
					options.appendArrows = this.sliderEl.querySelector( '.slick-arrows-container' );
					options.dots         = true;
					options.appendDots   = this.sliderEl.querySelector( '.slick-dots-container' );
					options.slide        = '.slide';
					options.rows         = 0;

					options.customPaging = ( slider, i ) => {
						const btn   = document.createElement( 'button' );
						const slide = slider.slides?.[ i ];
						const year  = slide?.dataset?.year?.trim();

						btn.type = 'button';
						btn.textContent = year || String( i + 1 );
						btn.setAttribute( 'aria-label', `Go to year ${ btn.textContent }` );

						return btn;
					};
					break;

				case 'discover-hero':
					options.arrows        = false;
					options.autoplay      = true;
					options.autoplaySpeed = 5000;
					options.dots          = true;
					options.appendDots    = this.sliderEl.querySelector( '.slick-dots-container' );
					options.infinite      = true;
					options.rows          = 0;
					options.slide         = '.slide';
					break;

				case 'discover-categories':
					options.arrows = true;
					options.dots   = false;
					options.rows   = 0;
					options.slide  = '.slide';

					options.responsive = [
						{
							breakpoint: 768,
							settings  : {
								slidesToShow  : 2,
								slidesToScroll: 2,
							},
						},
					];
					break;

				case 'component-resources':
					options.infinite = true;
					options.slide    = '.slide';
					options.rows     = 0;

					options.responsive = [
						{
							breakpoint: 768,
							settings  : {
								slidesToShow  : 2,
								slidesToScroll: 2,
							},
						},
						{
							breakpoint: 1024,
							settings  : {
								slidesToShow  : 3,
								slidesToScroll: 3,
							},
						},
					];
					break;

				case 'resources': {
					const count = this.slideCount;

					// Fewer than 3 cards: skip Slick so cards stay natural-width and
					// left-aligned instead of stretching to fill the carousel track.
					if ( 3 > count ) {
						this.sliderEl.querySelector( '.pr-20' )?.classList.remove( 'pr-20', 'lg:pr-24', 'xl:pr-32' );
						this.slidesEl.classList.add( 'resources-static' );
						this.$el.classList.remove( 'opacity-0' );
						return;
					}

					// options.infinite = true;
					options.slide    = '.slide';
					options.rows     = 0;

					options.responsive = [
						{
							breakpoint: 768,
							settings  : {
								slidesToShow  : 2,
								slidesToScroll: 2,
							},
						},
						{
							breakpoint: 1024,
							settings  : {
								slidesToShow  : 3,
								slidesToScroll: 3,
							},
						},
					];
					break;
				}

				case 'front-page-resources':

					// Scope slick's slide selector so its filter/unfilter helpers only
					// match the slide containers, not every descendant. Without this,
					// slick's internal querySelectorAll( '*' ) clears the inner DOM of
					// each cached slide on the second filter pass.
					options.slide = '.slide';

					// Disable buildRows. With options.slide set, _.slides tracks the
					// inner .slide elements rather than the row wrappers buildRows
					// creates, and cleanUpRows then wipes _.slider on every refresh
					// (which is what every responsive breakpoint change triggers).
					options.rows = 0;

					options.responsive = [
						{
							breakpoint: '48em',
							settings  : {
								slidesToShow  : 2,
								slidesToScroll: 2,
							},
						},
						{
							breakpoint: '80em',
							settings  : {
								slidesToShow  : 3,
								slidesToScroll: 3,
							},
						},
					];
					break;
				}

				// Desktop shows a static grid rather than a carousel. Slick's
				// 'unslick' responsive setting can't express that: it destroys the
				// instance, and destroy() disconnects slick's own ResizeObserver, so
				// the carousel never comes back when the viewport narrows again.
				// Drive that breakpoint ourselves instead.
				if ( 'discover-categories' === this.sliderName ) {
					this.bindCarouselBreakpoint( options, '( max-width: 1023.98px )' );
					return;
				}

				this.slidesEl.slick = slickInit( this.slidesEl, options );
				this.$el.classList.remove( 'opacity-0' );

				if ( 'component-resources' === this.sliderName ) {
					this.bindArrowVisibility();
				}

				if ( 'front-page-resources' === this.sliderName ) {
					this.bindResourceFilters();
				}
			},

			/**
			 * Hide the arrows container outright when every slide is already on
			 * screen. Slick only marks arrows `slick-disabled` in that case, and
			 * with `infinite: true` it does not even do that — it clones slides so
			 * the arrows stay live and scroll a track that has nowhere to go.
			 *
			 * Re-checked on slick's `breakpoint` event, which fires whenever the
			 * responsive settings swap and `slidesToShow` changes with them.
			 */
			bindArrowVisibility() {
				const arrows = this.sliderEl.querySelector( '.slick-arrows-container' );

				if ( ! arrows ) {
					return;
				}

				// Count real slides only: `infinite` clones slides into the track,
				// and those clones also carry the `.slide` class.
				const count = this.slidesEl.querySelectorAll( '.slide:not(.slick-cloned)' ).length;

				// The track's parent carries a right padding that reserves the
				// gutter the next arrow sits in. With no arrows that gutter is
				// just dead space, so collapse it too. Toggled via a class rather
				// than removing the Tailwind utilities, so widening the viewport
				// back to a scrollable state restores the gutter.
				const gutter = this.slidesEl.parentElement;

				const sync = () => {
					const shown = this.slidesEl.slick?.options?.slidesToShow ?? 1;
					const empty = count <= shown;

					arrows.classList.toggle( 'slick-arrows-container--empty', empty );
					gutter?.classList.toggle( 'slick-gutter--empty', empty );
				};

				sync();
				this.slidesEl.addEventListener( 'breakpoint', sync );
			},

			/**
			 * Init the slider only while `query` matches, tearing it down and
			 * rebuilding it on every crossing so both directions are handled.
			 *
			 * @param {Object} options Slick options.
			 * @param {string} query   Media query string.
			 */
			bindCarouselBreakpoint( options, query ) {
				const mql = window.matchMedia( query );

				const sync = () => {
					const initialized = this.slidesEl.classList.contains( 'slick-initialized' );

					if ( mql.matches && ! initialized ) {
						this.slidesEl.slick = slickInit( this.slidesEl, options );
					} else if ( ! mql.matches && initialized ) {
						this.slidesEl.slick.unslick();
					}

					this.$el.classList.remove( 'opacity-0' );
				};

				sync();
				mql.addEventListener( 'change', sync );
			},

			bindResourceFilters() {
				const slick      = this.slidesEl.slick;
				const filterList = document.querySelector( '[data-filter-group="front-page-resources"]' );

				if ( ! filterList ) {
					return;
				}

				// "See More" buttons live below the carousel, one per type with more
				// than 3 resources. Only the selected type's button is shown.
				const seeMoreButtons = Array.from( document.querySelectorAll( '[data-see-more]' ) );

				const applyFilter = () => {
					const active = filterList.querySelector( 'input[type="radio"]:checked' );

					// slick.slickFilter overwrites slidesCache on every call, so any
					// previously-filtered-out slides would be lost on the next filter.
					// Unfilter first to restore the full set, then apply the new filter.
					slick.slickUnfilter();

					// No pill selected: show the one-card-per-term set ( data-filter
					// "__all" ). A pill selected: show that type's resource-post cards.
					const value = active ? active.value : '__all';

					slick.slickFilter( `[data-filter="${ value }"]` );

					// Reveal only the selected type's "See More" button ( if any ).
					seeMoreButtons.forEach( btn => {
						btn.hidden = btn.dataset.seeMore !== value;
					});
				};

				// Reset to the initial state: no pill checked, term cards only.
				const resetToInitial = () => {
					filterList.querySelectorAll( 'input[type="radio"]:checked' ).forEach( radio => {
						radio.checked = false;
					});

					applyFilter();
				};

				// Start in the initial state. The browser may restore a pill as checked
				// on a back-navigation reload ( form-state restoration ) while slick
				// re-inits to the term-card view — clear any such pill so the two agree.
				resetToInitial();

				// Selecting a different pill filters to that type.
				filterList.addEventListener( 'change', event => {
					if ( event.target.matches( 'input[type="radio"]' ) ) {
						applyFilter();
					}
				});

				// Radios can't be toggled off natively. To allow "click the active pill
				// to clear", note whether the pill was already checked at pointer-down
				// ( before the browser flips its state ), then on the click undo it.
				// Using pointerdown avoids the label→input double-click that would
				// otherwise clear a freshly-selected pill.
				let clearOnClick = false;

				filterList.addEventListener( 'pointerdown', event => {
					const label = event.target.closest( 'label' );
					const radio = label ? label.querySelector( 'input[type="radio"]' ) : null;

					clearOnClick = !! radio && radio.checked;
				});

				filterList.addEventListener( 'click', event => {
					if ( ! clearOnClick ) {
						return;
					}

					clearOnClick = false;

					if ( event.target.closest( 'label' ) ) {
						event.preventDefault();
						resetToInitial();
					}
				});

				// Back-navigation that restores the page from bfcache does not re-run
				// this init, so also reset on pageshow to keep pills and carousel in
				// sync.
				window.addEventListener( 'pageshow', event => {
					if ( event.persisted ) {
						resetToInitial();
					}
				});
			},

			unpause() {
				this.slidesEl.slick.play();
			},
		}) );
	});
})();
